import type { FeedbackItem, FeedbackStatus } from '../types';

/**
 * Statuses that still represent work to do. Resolved and closed tickets are
 * left out of the export — the point of the file is "here's what's outstanding",
 * and closed tickets are hidden from the panel too.
 */
export const OUTSTANDING_STATUSES: FeedbackStatus[] = ['open', 'in-progress'];

const PRIORITY_ORDER: Record<string, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};

export interface MarkdownExportOptions {
  projectId?: string;
  /** Which statuses to include. Defaults to open + in-progress. */
  statuses?: FeedbackStatus[];
  generatedAt?: Date;
}

/** `https://site.com/about?x=1` → `/about`. Falls back to the raw string. */
function pagePath(url: string): string {
  try {
    const parsed = new URL(url);
    return parsed.pathname.replace(/\/+$/, '') || '/';
  } catch (_) {
    return url;
  }
}

function formatDateTime(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toISOString();
}

/** Fenced block, so multi-line copy survives intact rather than reflowing. */
function block(label: string, value: string): string[] {
  return ['', `**${label}**`, '', '```text', value.trim(), '```'];
}

function itemSection(item: FeedbackItem, index: number): string {
  const lines: string[] = [];
  const meta = item.pageMetadata;

  lines.push(`### ${index}. ${item.title || 'Untitled'}`);
  lines.push('');
  lines.push(`- **Ticket id:** \`${item.id}\``);
  lines.push(`- **Type:** ${item.type || 'general'}`);
  lines.push(`- **Status:** ${item.status} · **Priority:** ${item.priority}`);
  lines.push(
    `- **Raised by:** ${item.createdBy?.name || 'Unknown'} on ${formatDateTime(item.createdAt)}`
  );

  if (item.tags?.length) {
    lines.push(`- **Tags:** ${item.tags.join(', ')}`);
  }

  // Everything below is what actually locates the change in the codebase, so
  // it's worth being explicit even when only part of it is known.
  const componentName = item.componentName || meta?.componentName;
  if (componentName) {
    lines.push(`- **Component:** \`${componentName}\``);
  }
  if (meta?.elementInfo) {
    lines.push(`- **Element:** \`${meta.elementInfo}\``);
  }

  const selector = item.elementSelector || item.pinLocation?.elementSelector;
  if (selector) {
    lines.push(`- **CSS selector:** \`${selector}\``);
  }

  if (meta) {
    lines.push(
      `- **Seen on:** ${meta.deviceType} at ${meta.screenWidth}×${meta.screenHeight}`
    );
  }

  if (item.pinLocation) {
    const { pageX, pageY, percentX, percentY } = item.pinLocation;
    const percent =
      typeof percentX === 'number' && typeof percentY === 'number'
        ? ` (${percentX.toFixed(1)}% × ${percentY.toFixed(1)}% of the page)`
        : '';
    lines.push(
      `- **Pin position:** ${Math.round(pageX)}px × ${Math.round(pageY)}px${percent}`
    );
  }

  if (item.description) {
    lines.push(...block('Description', item.description));
  }

  if (item.type === 'copy-amendment' || item.currentCopy || item.newCopy) {
    if (item.currentCopy) lines.push(...block('Current copy', item.currentCopy));
    if (item.newCopy) lines.push(...block('Replace with', item.newCopy));
  }

  if (item.type === 'image-change') {
    const current = item.originalImageSrc || item.originalImageBackground;
    if (current) lines.push('', `**Current image:** ${current}`);
    if (item.replacementImageUrl) {
      lines.push('', `**Replacement image:** ${item.replacementImageUrl}`);
    }
  }

  if (item.comments?.length) {
    lines.push('', '**Comments**', '');
    for (const comment of item.comments) {
      const text = (comment.content || comment.text || '').replace(/\n/g, ' ');
      lines.push(
        `- ${comment.createdBy?.name || 'Unknown'} (${formatDateTime(comment.createdAt)}): ${text}`
      );
    }
  }

  // Screenshots are data URLs running to megabytes — noting they exist keeps
  // the file readable. Recordings live in storage, so the URL is useful.
  const attachments: string[] = [];
  if (item.screenshot) attachments.push('screenshot attached in the panel');
  if (item.designMockup) attachments.push('design mockup attached in the panel');
  if (item.screenRecording) attachments.push(`recording: ${item.screenRecording}`);
  if (attachments.length) {
    lines.push('', `**Attachments:** ${attachments.join(' · ')}`);
  }

  return lines.join('\n');
}

/**
 * Render outstanding markups as a markdown brief, grouped by the page they were
 * raised on, so it can be handed to a coding agent as the work list.
 */
export function buildMarkupMarkdown(
  feedbackItems: FeedbackItem[],
  options: MarkdownExportOptions = {}
): string {
  const statuses = options.statuses ?? OUTSTANDING_STATUSES;
  const generatedAt = options.generatedAt ?? new Date();

  const items = feedbackItems.filter((item) => statuses.includes(item.status));

  // Group by page, keyed on the full URL so two hosts don't merge, and sorted
  // by path so the file reads in a stable order run to run.
  const groups = new Map<string, FeedbackItem[]>();
  for (const item of items) {
    const url = item.pageMetadata?.url || 'Unknown page';
    if (!groups.has(url)) groups.set(url, []);
    groups.get(url)!.push(item);
  }

  const orderedGroups = Array.from(groups.entries()).sort((a, b) =>
    pagePath(a[0]).localeCompare(pagePath(b[0]))
  );

  const lines: string[] = [];

  lines.push('# Markup amendments');
  lines.push('');
  lines.push(
    `Generated ${generatedAt.toISOString()}${options.projectId ? ` · Project \`${options.projectId}\`` : ''}`
  );
  lines.push(
    `${items.length} outstanding ${items.length === 1 ? 'ticket' : 'tickets'} across ${orderedGroups.length} ${orderedGroups.length === 1 ? 'page' : 'pages'} (statuses: ${statuses.join(', ')}).`
  );
  lines.push('');
  lines.push(
    'Each ticket below is a requested amendment on a live page. Where a component, element or CSS selector is given, use it to locate the code; the pin position is where the reviewer clicked on the rendered page.'
  );

  if (items.length === 0) {
    lines.push('');
    lines.push('Nothing outstanding — every ticket is resolved or closed.');
    return lines.join('\n') + '\n';
  }

  for (const [url, groupItems] of orderedGroups) {
    const sorted = [...groupItems].sort((a, b) => {
      const byPriority =
        (PRIORITY_ORDER[a.priority] ?? 9) - (PRIORITY_ORDER[b.priority] ?? 9);
      if (byPriority !== 0) return byPriority;
      return (
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
    });

    lines.push('');
    lines.push('---');
    lines.push('');
    lines.push(`## ${pagePath(url)}`);
    lines.push('');
    lines.push(`- **URL:** ${url}`);
    if (sorted[0]?.pageMetadata?.title) {
      lines.push(`- **Page title:** ${sorted[0].pageMetadata.title}`);
    }
    lines.push(
      `- **Tickets:** ${sorted.length}`
    );
    lines.push('');

    sorted.forEach((item, i) => {
      lines.push(itemSection(item, i + 1));
      lines.push('');
    });
  }

  return lines.join('\n').replace(/\n{3,}/g, '\n\n').trimEnd() + '\n';
}

/** Suggested filename for an export, e.g. `markup-amendments-2026-08-13.md`. */
export function markupMarkdownFilename(date: Date = new Date()): string {
  return `markup-amendments-${date.toISOString().slice(0, 10)}.md`;
}

/** Save markdown to the user's downloads folder. */
export function downloadMarkdown(markdown: string, filename: string): void {
  const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Revoke on the next tick — revoking synchronously can cancel the download
  // in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
